/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// tslint:disable:max-file-line-count
// core
import { Component, Input, OnDestroy, OnInit, EventEmitter, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// import { CHANNELS, SETTINGS } from '@environment/settings';
import { Wove } from 'aspect.js/';
import { Router, ActivatedRoute } from '@angular/router';
// services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
import { LineLandingService } from '@experience/landing/common/services/line-landing.service';
import { ChangePlansService } from '@experience/change-plan-services/services/change-plans.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { CartNotEmptyModalService } from '@support/shared/services/cart-not-empty-modal.service';
// interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerDetails, AccountDetails } from '@support/api/proxies/responses/api-2007.interface';
import {
  CustomerAccountLineBillingDetails,
  ServiceDetails,
} from '@support/api/proxies/responses/api-2040.interface';
import {
  CustomerLineBillingDetailsBillBlock,
  CustomerLineBillingDetailsAccountLineDetailsCurrentService,
  CustomerLineBillingDetailsAccountLineDetailsCurrentPlan,
} from '@support/api/proxies/responses/api-2008.interface';
import { CustomerUsageSummaryResultsLines } from '@support/api/proxies/responses/api-2009.interface';
import { OrderSearchResults } from '@support/api/proxies/responses/api-2813.interface';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { CustomerSessionPassDetailsLines } from '@support/api/proxies/responses/api-2010.interface';
import {
  Line,
  CustomerAccountLineDeviceDetails,
} from '@support/api/proxies/responses/api-2022.interface';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import { CartLine, CartItem } from '@support/api/proxies/responses/api-cart-response.interface';
import { LineSummary } from '@support/api/proxies/responses/api-2006.interface';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
import { CartUtils } from '@support/cart-support/cart-utils';
// constants
import { ACCOUNT_LANDING_CONSTANTS } from '@experience/landing/common/constants/account-landing.constants';
import {
  CART_ITEM_STATUS,
  PLAN_SERVICES_CONSTANTS,
} from '@experience/landing/common/constants/line-details.constants';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import {
  COMMONLANDING_MODULE_COMPONENTS,
  LINE_LANDING_CONSTANTS,
  ORDER_TYPES,
} from '@experience/landing/common/components/line-landing/line-landing.constants';
import {
  SYMBOL_CONSTANTS,
  LINE_CONSTANTS,
} from '@support/shared/constants/device.constants';
import { APP_ROUTE_CONSTANTS } from '@root/shared/constants/app-route.constants';
// component
import { DynamicModalComponent } from '@support/shared/widgets/components/dynamic-modal/dynamic-modal.component';
// model
import { LinePendingOrdersModel } from '@experience/landing/common/models/line-pending-orders.model';
// pdl
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

@Wove({ moduleName: COMMONLANDING_MODULE_COMPONENTS.COMMONLANDING_MODULE_NAME })
@Component({
  selector: 'tmo-line-details',
  templateUrl: './line-details.component.html',
  styleUrls: ['../../../../../../style-sheets/components/html-spinner.component.scss'],
})
export class LineDetailsComponent implements OnInit, OnDestroy {
  @Input() public contentData: ConfigData;
  @Input() public lineDetails?: LineSummary;
  @Input() public lineSessionPassDetails: CustomerSessionPassDetailsLines;
  @Input() public lineBillingDetails: CustomerAccountLineBillingDetails;
  @Input() public billCycle?: CustomerLineBillingDetailsBillBlock;
  @Input() public customerData?: CustomerDetails;
  @Input() public isCPS?: boolean;
  @Input() public lineUsage?: CustomerUsageSummaryResultsLines;
  @Input() public customerBillingDetails?: CustomerAccountBillingDetails;
  @Input() public linePendingOrdersModel?: LinePendingOrdersModel;
  @Input() public selectedOption?: string;
  /**
   * @type {number}
   * @memberof UsageDetailComponent
   */
  @Input() public lineIndex: number;
  /**
   * setter to check if we have navigated from rate plans page and set the new selected plan
   */
  @Input() public set setChangePlanPage(page: string) {
    const currentPage = page ? page.toLowerCase() : '';
    this.isPlanPage = currentPage === ACCOUNT_LANDING_CONSTANTS.PLANS;
    this.isManagePendingOrderPage =
      currentPage.toLowerCase() === ACCOUNT_LANDING_CONSTANTS.MANAGE_PENDING_ORDERS;
    this.newPlan = !this.isManagePendingOrderPage
      ? this.stateStoreUtilsService.getNewPlanForLine()
      : null;
  }
  /**
   * stores the pending order count
   */
  @Input() public pendingOrdersCount?: string;

  public selectedPlan: CustomerLineBillingDetailsAccountLineDetailsCurrentPlan;
  /**
   * Dynamic modal for send Plan pop up
   */
  public sendPlanModal: DynamicModal;
  /**
   * model to store active products
   */
  public activeProductsModal: DynamicModal;
  /**
   * stores image of device
   */
  public imageUrl: string;
  /**
   * Stores value of SYMBOL_CONSTANTS
   */
  public symbolConstants: Object;
  /**
   * Stores value of SYMBOL_CONSTANTS
   */
  public lineConstants: Object;
  /**
   * Stores value of CANCELLED status
   */
  public lineStatusCancelled: string;
  /**
   * opens soft good modal
   */
  public softGoodsModalLink: string;
  /**
   * stores lineType from account constants
   */
  public lineType: string;
  /**
   * stores third party services of customer
   */
  public thirdPartyServiceArray: CustomerLineBillingDetailsAccountLineDetailsCurrentService[];
  /**
   * fetches account details
   */
  public accountData: AccountDetails;
  /**
   * fetches data of lines which are to be displayed
   */
  public lines: Line;
  /**
   * Flag to check if assisted flow
   */
  // public isAssistedFlow: boolean;
  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * Variable to hold new plan
   */
  public newPlan: PlanOption;
  /**
   * stores the value of current CPS page
   */
  public isManagePendingOrderPage: boolean;
  /**
   * stores the value of current CPS page
   */
  public isPlanPage: boolean;
  /**
   * stores the addedPlan in cart
   */
  public addedPlan: CartItem;
  /**
   * stores the retained plan in cart
   */
  public retainedPlan: CartItem;
  /**
   * stores the removedPlan in cart
   */
  public removedPlan: CartItem;
  /**
   * stores the addedPlan in cart
   */
  public removedService: CartItem[];
  /**
   * stores the addedPlan in cart
   */
  public addedService: CartItem[];
  /**
   * Flag to check permission
   */
  public isPermissionAvailable: boolean;
  /**
   * flag to check permission
   */
  public isUpgradePermissionAvailable: boolean;
  /**
   * stores the added session pass in cart
   */
  public addedSessionPass: CartItem[];
  /**
   * stores the removed session pass in cart
   */
  public removedSessionPass: CartItem[];
  /**
   * stores the addedPlan in cart
   */
  public newSelectedPlan: CartItem;
  /**
   * stores primary constant value
   */
  public primaryConstant: string;
  /**
   * stores the addedPlan in cart
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * stores boolean value if page is Line landing
   */
  public serviceArray: CustomerLineBillingDetailsAccountLineDetailsCurrentService[];
  @Input() public isLineLandingPage: boolean;
  /**
   * dynamic modal component reference object
   */
  @ViewChild('dynamicModal', { static: false }) public dynamicModal: DynamicModalComponent;

  /**
   * stores constant value of Permission Constant
   */
  public permissionConstants: KeyValueMap;
  /**
   * Boolean flag to test if any future product or pending order available
   */
  public futureProductsAvailable: boolean;

  constructor(
    private cartSupportService: CartSupportService,
    private cartNotEmptyModalService: CartNotEmptyModalService,
    protected objectService: ObjectService,
    protected stateStoreUtilsService: StateStoreUtilsService,
    private featureToggleUtilService: FeatureToggleUtilService,
    protected commonUtilService: CommonUtilService,
    protected userService: UserService,
    protected lineLandingService: LineLandingService,
    public routeSnapshot: ActivatedRoute,
    public router: Router,
    public changePlansService: ChangePlansService
  ) {
    this.imageUrl = '';
    this.softGoodsModalLink = '';
    this.sendPlanModal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'send-plan-modal',
    };
    this.activeProductsModal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'active-products-modal',
    };
    this.ngUnsubscribe = new Subject<void>();
    this.symbolConstants = SYMBOL_CONSTANTS;
    this.futureProductsAvailable = false;
    this.lineConstants = LINE_CONSTANTS;
    this.primaryConstant = ACCOUNT_LANDING_CONSTANTS.PRIMARY;
  }

  public ngOnInit(): void {
    if (!this.lineBillingDetails) this.lineBillingDetails = this.stateStoreUtilsService.getCustomerAccountLineBillingDetail(this.stateStoreUtilsService.getLineId());
    if (!this.customerBillingDetails) this.customerBillingDetails = this.stateStoreUtilsService.getBillingBlockDetails();
    this.isPermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
      this.router.url,
      PERMISSION_CONSTANTS.ORDER_DEVICE
    );
    this.isUpgradePermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
      this.router.url,
      PERMISSION_CONSTANTS.PRE_ORDER_DEVICE
    );
    this.selectedOption = this.objectService.get(
      this.linePendingOrdersModel,
      'ordersByType.0.value.0.orderId'
    );
    this.lineStatusCancelled = PLAN_SERVICES_CONSTANTS.CANCELLED;
    this.permissionConstants = PERMISSION_CONSTANTS;
    // this.isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL ||
    // SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE);
    this.activateLink();
    this.getCartData();
    this.serviceArray = this.objectService.get(this.lineBillingDetails, 'lineDetails.services');
    if (this.serviceArray) {
      this.objectService.set(this.lineBillingDetails, 'lineDetails.services', []);
      this.thirdPartyServiceArray = [];
      this.serviceArray.forEach((service: ServiceDetails) => {
        if (service.thirdPartyService) {
          this.thirdPartyServiceArray.push(service);
        } else {
          this.lineBillingDetails.lineDetails.services.push(service);
        }
      });
    }
    const unfilteredAccountData = this.objectService.get(this.customerData, 'accounts');
    this.accountData = unfilteredAccountData
      ? unfilteredAccountData.filter(
          (account: AccountDetails) =>
            account.accountNumber === this.stateStoreUtilsService.getAccountId()
        )
      : '';
    const type: string = this.objectService.get(this.lineDetails, 'lineType');
    if (type) {
      this.lineType =
        type.toLocaleLowerCase() === ACCOUNT_LANDING_CONSTANTS.DUPLICATE
          ? ACCOUNT_LANDING_CONSTANTS.DUPLICATE_SYMBOL
          : ACCOUNT_LANDING_CONSTANTS.VIRTUAL_SYMBOL;
    }
    this.getDeviceDetails();
    const lineOfServiceId = this.objectService.get(this.lineDetails, 'lineOfServiceId');
    const orderDetails = this.stateStoreUtilsService.getPendingOrders(lineOfServiceId);
    if (orderDetails) {
      this.checkIfFutureProductsAvailable(orderDetails);
    } else {
      this.lineLandingService.pendingOrderSubject$
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((orderResults: OrderSearchResults[]) => {
          this.checkIfFutureProductsAvailable(orderResults);
        });
    }
    this.lineLandingService.selectedMsidin.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.getDeviceDetails();
    });
  }

  /**
   * Component lifecycle hook to destroy the component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Checks which link to be displayed according to the conditions
   * @returns string
   */
  public activateLink(): string {
    const isActiveProduct =
      this.objectService.get(this.lineBillingDetails, 'lineDetails.plan.length') ||
      this.objectService.get(this.lineBillingDetails, 'lineDetails.services.length') ||
      this.objectService.get(this.lineSessionPassDetails, 'lineDetails.passDetails.active.length');
    const isFutureProduct = !!this.objectService.get(
      this.lineSessionPassDetails,
      'lineDetails.passDetails.future.length'
    );
    if (isActiveProduct && isFutureProduct && this.contentData) {
      this.softGoodsModalLink = this.contentData.viewActiveAndFutureProductCharges;
    } else {
      if (isFutureProduct && this.contentData) {
        this.softGoodsModalLink = this.contentData.viewFutureProductCharges;
      } else if (isActiveProduct && this.contentData) {
        this.softGoodsModalLink = this.contentData.viewActiveAndFutureProduct;
      }
    }
    return this.softGoodsModalLink;
  }

  /**
   * Method to check if any future product or pending order is available
   * @returns void
   */
  public checkIfFutureProductsAvailable(orderResults: OrderSearchResults[]): void {
    const futureCharges = this.objectService.get(
      this.lineSessionPassDetails,
      'lineDetails.passDetails.futurePassTotal'
    );
    const msisdn = this.stateStoreUtilsService.getMsisdn();
    const lookUpParams: { [name: string]: string } = {};
    lookUpParams.msisdn = msisdn;
    const orderSearchResultsFilterList: OrderSearchResults[] = orderResults.filter(
      (order: OrderSearchResults) => {
        return (
          (order.orderStatus === LINE_LANDING_CONSTANTS.INPROCESS ||
            order.orderStatus === LINE_LANDING_CONSTANTS.RECEIVED) &&
          order.msisdn === this.stateStoreUtilsService.getMsisdn() &&
          ORDER_TYPES.indexOf(order.orderType) >= 0
        );
      }
    );
    this.futureProductsAvailable =
      (orderSearchResultsFilterList && orderSearchResultsFilterList.length !== 0) ||
      (futureCharges && futureCharges !== '0.0');
    this.futureProductsAvailable = !!this.futureProductsAvailable;
  }

  /**
   * Checks which line data to be displayed
   * @returns void
   */
  public customerLines(): void {
    this.imageUrl = '';
    const image = this.objectService.get(this.lines, 'imageURL');
    if (this.commonUtilService.isNotEmpty(this.lines)) {
      if (!image) {
        this.imageUrl = this.commonUtilService.getImageUrl(this.imageUrl);
      } else {
        this.imageUrl = this.commonUtilService.getImageUrl(image);
      }
    } else {
      this.imageUrl = this.commonUtilService.getImageUrl(image);
    }
  }
  /**
   * Function to handle null values for product image
   * @param value - the value to check for nullability
   */
  public getImageUrl(value: string): string {
    return this.commonUtilService.getImageUrl(value);
  }
  /**
   * Method to open Send Plan pop up
   * @param event : Event to decide whether to open
   * @returns void
   */
  public openModal(event: EventEmitter<Object>, index: number): void {
    this.triggerEvent = event;
    this.selectedPlan = this.lineBillingDetails.lineDetails.plan;
    this.dynamicModal.displayModal = true;
    this.dynamicModal.focusModal();
  }
  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public openActiveProductsModal(event?: EventEmitter<Object>): void {
    this.triggerEvent = event;
    this.selectedOption = this.objectService.get(
      this.linePendingOrdersModel,
      'ordersByType.0.value.0.orderId'
    );
    this.activeProductsModal.toggleModalEmitter.emit({ open: true, close: false });
  }

  /**
   * Method to go to the order summary page of the selected orderId
   * @returns void
   */
  public goToOrderSummary(): void {
    this.stateStoreUtilsService.setCustomerOrderId(this.selectedOption);
    this.router.navigate([APP_ROUTE_CONSTANTS.ORDER_SUMMARY_URL]);
  }
  /**
   * Method called on selection of radio button
   * @param orderId : Stores the orderId of selected radio button
   */
  public onSelect(orderId: string): void {
    this.selectedOption = orderId;
  }

  /**
   * Method to open Plan modal for Retained and Removed services
   * @param event :Event to decide whether to open
   * @param item : Removed or Retained Plan
   * @returns void
   */
  public openPlanModal(event: EventEmitter<Object>, item: CartItem): void {
    this.triggerEvent = event;
    const price = this.objectService.get(item, 'price.priceSummary.monthlyPrice.listPrice.display');
    this.selectedPlan = {
      code: item.offerId,
      description: item.offerName,
      price: price || '',
      longDescription: item.description,
    };
    this.dynamicModal.displayModal = true;
    this.dynamicModal.focusModal();
  }
  /**
   * Method to open Send Plan pop up for a New Plan
   * @param event : Event to decide whether to open
   * @returns void
   */
  public openNewPlanModal(event: EventEmitter<Object>, index: number): void {
    this.triggerEvent = event;
    this.selectedPlan = {
      code: this.newPlan.attribute,
      description: this.newPlan.label,
      price: this.newPlan.monthlyPrice.price,
      longDescription: this.newPlan.longDescription,
    };
    this.dynamicModal.displayModal = true;
    this.dynamicModal.focusModal();
  }
  /**
   * Method to close Send Plan pop up
   * @returns void
   */
  public closeModal(): void {
    this.dynamicModal.displayModal = false;
    const target = 'target';
    this.triggerEvent[target].focus();
    this.activeProductsModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
  }
  /**
   * Sets the url of the page on click of demand pass link
   * @param url : stores the value of the url
   */
  public setPreviousUrl(url: string): void {
    this.stateStoreUtilsService.setPreviousUrl(url);
  }

  /**
   * get line substatus.
   * @param {Line} lineDetails
   * @returns {string}
   * @memberof LineDetailsComponent
   */
  public getSubStatus(line: LineSummary): string {
    return this.userService.getLineStatus(line);
  }

  /**
   * Method to Navigate to Line Upgrade Flow
   */
  public navigateCheckForLineUpgrade(lineId?: string): void {
    const cart = this.stateStoreUtilsService.getCart();
    this.stateStoreUtilsService.setSelectedUpgradeLineId(lineId);
    if (this.cartSupportService.isCartNotEmpty(cart)) {
      const route = {
        navigateToRoute: ACCOUNT_LANDING_CONSTANTS.BROWSE_DEVICES,
        queryParams: { isUpgrade: true },
      };
      this.cartNotEmptyModalService.openModal(route);
    } else {
      // if (this.commonUtilService.isRetailChannel()) {
      //   this.stateStoreUtilsService.setUpgradeDevice(true);
      //   this.router.navigate(['plan-services']);
      // } else {
      this.router.navigate([ACCOUNT_LANDING_CONSTANTS.BROWSE_DEVICES], {
        queryParams: { isUpgrade: true },
      });
      // }
    }
  }
  /**
   * Method to retrieve Added, Removed and Retained Cart items from Cart Cache
   * @param cartData : Cart reference
   * @returns void
   */
  private getAddedAndRemovedItems(cartData: CartLine[]): void {
    cartData.forEach((cartLineData: CartLine) => {
      this.addedPlan = CartUtils.getCartItemFromLine(cartLineData, CART_ITEM_STATUS.ADD, 'plan');
      this.retainedPlan = CartUtils.getCartItemFromLine(
        cartLineData,
        CART_ITEM_STATUS.RETAIN,
        'plan'
      );
      this.removedPlan = CartUtils.getCartItemFromLine(
        cartLineData,
        CART_ITEM_STATUS.REMOVE,
        'plan'
      );
      const allAddedServices = CartUtils.getCartItemAsArrayFromLine(
        cartLineData,
        CART_ITEM_STATUS.ADD,
        'services'
      );
      const allRemovedServices = CartUtils.getCartItemAsArrayFromLine(
        cartLineData,
        CART_ITEM_STATUS.REMOVE,
        'services'
      );
      this.addedService = this.lineLandingService.groupServicesByType(
        allAddedServices,
        CART_ITEM_STATUS.ADD,
        false
      );
      this.removedService = this.lineLandingService.groupServicesByType(
        allRemovedServices,
        CART_ITEM_STATUS.REMOVE,
        false
      );
      this.addedSessionPass = this.lineLandingService.groupServicesByType(
        allAddedServices,
        CART_ITEM_STATUS.ADD,
        true
      );
      this.removedSessionPass = this.lineLandingService.groupServicesByType(
        allRemovedServices,
        CART_ITEM_STATUS.REMOVE,
        true
      );
    });
  }

  /**
   * Method to get data from cart if available
   * @returns void
   */
  private getCartData(): void {
    const cartLineData = this.stateStoreUtilsService.getCart();
    if (cartLineData) {
      this.getAddedAndRemovedItems(cartLineData.lines);
    }
  }
  /**
   * Loads the device details from API_2022
   * @returns void
   */
  private getDeviceDetails(): void {
    this.userService
      .getCustomerAccountLineDeviceDetails(this.stateStoreUtilsService.getMsisdn())
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (customerAccountLineDeviceDetails: CustomerAccountLineDeviceDetails) => {
          if (this.commonUtilService.isNotEmpty(customerAccountLineDeviceDetails)) {
            this.lines = this.objectService.get(
              customerAccountLineDeviceDetails,
              'devicedetails.lines.0'
            );
            this.customerLines();
            this.lineLandingService.deviceDetailsLoaded.next(this.lines);
          }
        },
        () => {
          this.lineLandingService.setErrorMessage(this.contentData.deviceDetailsError);
        }
      );
  }
}
